// @flow

import * as React from 'react';
import { connectAdvanced } from 'react-redux';
import buildOptionsKey from 'app/common/redux/reducers/resources/buildOptionsKey';
import logStats from 'app/common/lib/logStats';
import PropTypes from 'prop-types';
import shallowEqual from 'app/packages/shallow-equal';
import type { Action } from 'app/common/redux/actions/types';
import type { State as HistoryState } from 'app/common/redux/reducers/history';
import type { State as ReduxState } from 'app/common/redux/reducers/types';
import typeof { resources } from 'node/resources/routes';
import type { Resource, ResourceError } from 'app/packages/pinterest-flow-types/resource';
import {
  resourceFetch,
  resourceFetchMore,
  resourcePrefetchRead,
} from 'app/common/redux/actions/resourceFetch';

export const END_BOOKMARK = '-end-';

export type { ResourceError };

export type { Resource };

type OptionsOrThunk = Object | ((props: Object) => Object);

type ResourceName = $Keys<resources>;

type Config<Key> = {
  name: ResourceName,
  key: Key,
  options?: OptionsOrThunk,
};

type Options = {
  acceptPrefetch?: boolean,
  allowStale?: false,
  enabledRouteRefresh?: boolean,
  moduleRenderOptions?: Object,
  headers?: Object,
  traceLoggerHandlerName?: string,

  // NOTE(jackhsu): This option supports conditionally disabling resource fetch. For example,
  // a typeahead provider may want to show autocomplete search results only when search query is
  // at least 2 characters long. If query is less than 2 characters long, show the default
  // recommendations in memory. In this example, we can use this option:
  // withResource(..., { dangerouslyDisableFetch: (props) => props.query.length < 2 })
  //
  // An alternative approach is to conditionally render component without resource and component
  // with resource depending on whether or not we need to fetch. However, the problem with this
  // approach is that, when a different component is rendered, the entire subtree gets unmounted,
  // and all the local states in the subtree get destroyed.
  //
  dangerouslyDisableFetch?: (props: Object) => boolean,
};

type Props<Namespace> = {
  dispatch: (action: Action) => void,
  [name: Namespace]: Resource<Object>,
  history: HistoryState,
};

type State = {
  isRefreshing: boolean,
  shouldInvalidatePrefetch?: boolean,
};

export function buildOptionsOrThunkKey(options: OptionsOrThunk = {}, props: Object) {
  return buildOptionsKey(typeof options === 'function' ? options(props) : options);
}

export function getOptions(options: OptionsOrThunk = {}, props: Object) {
  return typeof options === 'function' ? options(props) : options;
}

export default function withResource<Namespace: $Subtype<string>>(
  resource: Config<Namespace>,
  options: Options = {},
) {
  const nameSpace: Namespace = resource.key;

  // Optimized redux store watcher/extractor
  function selectorFactory(dispatch: (action: Action) => void) {
    const FALLBACK_REF = {};
    let prev = { fetching: FALLBACK_REF, data: FALLBACK_REF };
    let cachedOwnProps;
    let cachedNameSpace;
    let cachedAdditionalProps;
    let cachedMergedProps;
    const didReadResource = {};

    function updateMergedProps(
      ownProps?: Object,
      nameSpaceProps?: Object,
      additionalProps?: Object,
    ) {
      if (ownProps) {
        // update cachedOwnProps if passed in
        cachedOwnProps = ownProps;
      }
      if (nameSpaceProps) {
        // update cachedNameSpace if passed in
        cachedNameSpace = nameSpaceProps;
      }
      if (additionalProps) {
        // update cachedAdditionalProps if passed in
        cachedAdditionalProps = additionalProps;
      }
      // update merged props
      cachedMergedProps = {
        ...cachedOwnProps,
        dispatch,
        [nameSpace]: cachedNameSpace,
        ...cachedAdditionalProps,
      };
      return cachedMergedProps;
    }

    return function buildWithResourceProps({ resources, history }: ReduxState, ownProps: Object) {
      const { fetching: storeFetching, data: storeData } = resources;

      const propsChanged = !(cachedOwnProps && shallowEqual(cachedOwnProps, ownProps));
      const stateChanged = !(storeFetching === prev.fetching && storeData === prev.data);

      // Early bail if nothing changed in state or props
      if (!propsChanged && !stateChanged && cachedMergedProps) {
        return cachedMergedProps;
      }

      // Build the new hash key
      const optionsKey = buildOptionsOrThunkKey(resource.options, ownProps);

      // only props changed - this will trigger update
      if (propsChanged && !stateChanged && cachedMergedProps) {
        return updateMergedProps(ownProps);
      }

      // Use a fallback for reference equality
      const currFetching = storeFetching[resource.name] || FALLBACK_REF;
      const prevFetching = prev.fetching[resource.name] || FALLBACK_REF;
      const currData = storeData[resource.name] || FALLBACK_REF;
      const prevData = prev.data[resource.name] || FALLBACK_REF;

      // Only log if it's the first time to read this resource.
      // Subsequent data fetches within the same session will all probably have
      // some data in here, and once we're on the next page it's all from ajax
      // responses anyway. Logging more is noisey.
      if (!didReadResource[resource.name]) {
        if (Object.keys(currData).length === 0) {
          logStats.increment('webapp.withResource.no-cache', 0.05, {
            resource: resource.name,
            cache_size: 0,
            level: 'warn',
            v: 2,
            xFoundKeys: null,
            xExpectedKey: optionsKey,
          });
        } else if (!(optionsKey in currData)) {
          logStats.increment('webapp.withResource.populated-cache-miss', 0.05, {
            resource: resource.name,
            cache_size: Object.keys(currData).length,
            level: 'error',
            v: 2,
            xFoundKeys: JSON.stringify(Object.keys(currData)),
            xExpectedKey: optionsKey,
          });
        } else {
          logStats.increment('webapp.withResource.cache-hit', 0.05, {
            resource: resource.name,
            cache_size: Object.keys(currData).length,
            level: 'success',
            v: 2,
          });
        }
      }
      didReadResource[resource.name] = true;

      // Has the store changed for this resource?
      const hasPathChanged =
        currFetching !== prevFetching ||
        currData !== prevData ||
        currFetching[optionsKey] !== prevFetching[optionsKey] ||
        currData[optionsKey] !== prevData[optionsKey];
      // bail - resource hasn't changed and props haven't changed
      if (!propsChanged && !hasPathChanged && cachedMergedProps) {
        return cachedMergedProps;
      }

      // resource hasn't changed but props changed - this will trigger update
      if (propsChanged && !hasPathChanged && cachedMergedProps) {
        return updateMergedProps(ownProps);
      }

      // Save old references for equality checks
      prev = { fetching: storeFetching, data: storeData };

      // Cache new namespaced props
      const { data, error, nextBookmark, isPrefetch, prefetchHasBeenRead } =
        currData[optionsKey] || {};
      const isFetching = Boolean(currFetching[optionsKey]);
      const isLoaded = Boolean(currData[optionsKey]);
      const isAtEnd = isLoaded && !isFetching && nextBookmark === END_BOOKMARK;

      return updateMergedProps(
        ownProps,
        {
          data,
          error,
          isAtEnd,
          isFetching,
          isLoaded,
          nextBookmark,
          isPrefetch,
          prefetchHasBeenRead,
        },
        { history },
      );
    };
  }

  return function withResourceFactory(Subject: React.ComponentType<*>) {
    class SubjectWithResource extends React.Component<Props<Namespace>, State> {
      static WrappedComponent = Subject;
      // $FlowFixMe: new errors found from flow 0.96 upgrade
      static displayName = `WithResource[${resource.name}](${Subject.displayName || Subject.name})`;
      static propTypes = {
        dispatch: PropTypes.func.isRequired,
        [nameSpace]: PropTypes.shape({
          data: PropTypes.any,
          error: PropTypes.any,
          isAtEnd: PropTypes.bool.isRequired,
          isFetching: PropTypes.bool.isRequired,
          isLoaded: PropTypes.bool.isRequired,
        }),
      };

      state = { isRefreshing: false };

      UNSAFE_componentWillMount() {
        const { dispatch, history, [nameSpace]: nameSpacedData } = this.props;

        if (
          nameSpacedData.isLoaded &&
          options.enabledRouteRefresh &&
          history.current &&
          history.current.action !== 'POP' &&
          !this.fetchDisabled()
        ) {
          // if enabledRouteRefresh is true and the current location is not a POP
          // set isRefreshing to true before the first render pass
          this.setState({ isRefreshing: true });
        } else if (
          nameSpacedData.isLoaded &&
          history.current &&
          history.current.action !== 'POP' &&
          options.acceptPrefetch &&
          nameSpacedData.isPrefetch &&
          !nameSpacedData.prefetchHasBeenRead &&
          !this.fetchDisabled()
        ) {
          // Handles prefetching for non route-refresh components like profile
          // Marks the prefetched data as read when the component mounts for the first time
          // (if the page is retrieved from cache, i.e. a "POP" action, then we don't need to do anything)
          dispatch(resourcePrefetchRead(resource.name, getOptions(resource.options, this.props)));
        }
      }

      componentDidMount() {
        const { [nameSpace]: nameSpacedData } = this.props;
        const { isRefreshing } = this.state;

        if (options.allowStale === false || !nameSpacedData.isLoaded || isRefreshing) {
          this.loadData(this.props);
        }
      }

      UNSAFE_componentWillReceiveProps(nextProps: Props<Namespace>) {
        const { [nameSpace]: nameSpacedData } = this.props;
        const { isRefreshing } = this.state;

        const nextOptionsKey = buildOptionsOrThunkKey(resource.options, nextProps);
        if (nextOptionsKey !== this.optionsKey) {
          this.loadData(nextProps);
          this.optionsKey = nextOptionsKey;
        }

        if (isRefreshing && nameSpacedData.data !== nextProps[nameSpace].data) {
          // if we're currently refreshing and the resource has finished loading
          // update the state before the next render pass
          this.setState({ isRefreshing: false });
        }
      }

      optionsKey: string = buildOptionsOrThunkKey(resource.options, this.props);

      fetchDisabled = (props?: Props<Namespace>) =>
        !!options.dangerouslyDisableFetch && options.dangerouslyDisableFetch(props || this.props);

      loadData = (props: Props<Namespace>) => {
        if (this.fetchDisabled(props)) {
          return;
        }
        props.dispatch(
          resourceFetch(
            resource.name,
            getOptions(resource.options, props),
            options.moduleRenderOptions,
            options.headers,
            options.traceLoggerHandlerName,
          ),
        );
      };

      fetchMore = () => {
        const { dispatch } = this.props;

        if (this.fetchDisabled()) {
          return;
        }

        dispatch(
          resourceFetchMore(
            resource.name,
            getOptions(resource.options, this.props),
            options.moduleRenderOptions,
            options.headers,
            options.traceLoggerHandlerName,
          ),
        );
      };

      refreshData = () => {
        if (this.fetchDisabled()) {
          return;
        }
        this.loadData(this.props);
        this.setState({ isRefreshing: true });
      };

      render() {
        const { [nameSpace]: nameSpacedData } = this.props;
        const { isRefreshing } = this.state;

        nameSpacedData.fetchMore = this.fetchMore;
        nameSpacedData.refreshData = this.refreshData;
        nameSpacedData.isRefreshing = isRefreshing;
        return <Subject {...this.props} />;
      }
    }

    // $FlowFixMe: new errors found from react-redux upgrade
    return connectAdvanced(selectorFactory)(SubjectWithResource);
  };
}
